<template>
  <div class="walkthroughs-container">
    <Sidebar />
    <h1>Walkthroughs</h1>
    
    <!-- Search/Filter Section -->
    <div class="search-section">
      <div class="search-box">
        <input 
          type="number" 
          v-model="searchId" 
          placeholder="Enter walkthrough ID" 
          min="1"
          @keyup.enter="fetchWalkthroughById"
        />
        <button @click="fetchWalkthroughById" class="search-button">Search</button>
      </div>
      <div class="action-buttons">
        <button @click="fetchAllWalkthroughs" class="view-all-button">View All</button>
        <button @click="showCreateForm = true" class="create-button">Create New</button>
      </div>
    </div>
    
    <!-- Loading and Error States -->
    <div v-if="loading" class="loading">Loading...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    
    <!-- Create Walkthrough Form -->
    <div v-if="showCreateForm" class="create-form-overlay">
      <div class="edit-form">
        <div class="form-header">
          <h2>Create New Walkthrough</h2>
          <button @click="showCreateForm = false" class="close-button">×</button>
        </div>
        
        <div class="form-group">
          <label for="new-title">Title (English)</label>
          <input 
            type="text" 
            id="new-title" 
            v-model="newWalkthrough.title" 
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="new-title_ar">Title (Arabic)</label>
          <input 
            type="text" 
            id="new-title_ar" 
            v-model="newWalkthrough.title_ar" 
            class="form-control"
            dir="rtl"
          />
        </div>
        
        <div class="form-group">
          <label for="new-description">Description</label>
          <QuillEditor 
            id="new-description" 
            v-model:content="newWalkthrough.description" 
            label="Description"
            :toolbar="toolbarOptions"
            placeholder="Enter walkthrough description"
          />
        </div>
        
        <div class="form-group">
          <label for="new-video">Video URL</label>
          <input 
            type="text" 
            id="new-video" 
            v-model="newWalkthrough.video" 
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label class="checkbox-label">
            <input 
              type="checkbox" 
              v-model="newWalkthrough.isOn" 
              :true-value="1" 
              :false-value="0"
            />
            Active
          </label>
        </div>
        
        <div class="form-actions">
          <button @click="createWalkthrough" class="save-button" :disabled="creating">
            {{ creating ? 'Creating...' : 'Create Walkthrough' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Single Walkthrough View -->
    <div v-else-if="singleWalkthrough" class="single-walkthrough">
      <div class="back-button" @click="fetchAllWalkthroughs">
        <span>← Back to All Walkthroughs</span>
      </div>
      
      <!-- Edit Mode Toggle -->
      <div class="edit-actions">
        <button 
          @click="toggleEditMode" 
          :class="['toggle-button', isEditing ? 'cancel-button' : 'edit-button']"
        >
          {{ isEditing ? 'Cancel Editing' : 'Edit Walkthrough' }}
        </button>
        <button 
          @click="deleteWalkthrough(singleWalkthrough.id)" 
          class="delete-button"
        >
          Delete Walkthrough
        </button>
      </div>
      
      <!-- View Mode -->
      <div v-if="!isEditing" class="landing-card detailed">
        <div class="landing-header">
          <h2>{{ singleWalkthrough.title }}</h2>
          <h3>{{ singleWalkthrough.title_ar }}</h3>
        </div>
        <div class="landing-content">
          <p v-if="singleWalkthrough.description" class="description">{{ singleWalkthrough.description }}</p>
          <div v-if="singleWalkthrough.video" class="video-container">
            <video controls>
              <source :src="singleWalkthrough.video" type="video/mp4">
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
        <div class="landing-footer">
          <span class="status" :class="{ 'active': singleWalkthrough.isOn === 1 }">
            {{ singleWalkthrough.isOn === 1 ? 'Active' : 'Inactive' }}
          </span>
          <div class="dates">
            <p>Created: {{ formatDate(singleWalkthrough.created_at) }}</p>
            <p>Updated: {{ formatDate(singleWalkthrough.updated_at) }}</p>
          </div>
        </div>
      </div>
      
      <!-- Edit Mode -->
      <div v-else class="edit-form">
        <div class="form-group">
          <label for="title">Title (English)</label>
          <input 
            type="text" 
            id="title" 
            v-model="editedWalkthrough.title" 
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="title_ar">Title (Arabic)</label>
          <input 
            type="text" 
            id="title_ar" 
            v-model="editedWalkthrough.title_ar" 
            class="form-control"
            dir="rtl"
          />
        </div>
        
        <div class="form-group">
          <label for="description">Description</label>
          <QuillEditor 
            id="description" 
            v-model:content="editedWalkthrough.description" 
            label="Description"
            :toolbar="toolbarOptions"
            placeholder="Enter walkthrough description"
          />
        </div>
        
        <div class="form-group">
          <label for="video">Video URL</label>
          <input 
            type="text" 
            id="video" 
            v-model="editedWalkthrough.video" 
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label class="checkbox-label">
            <input 
              type="checkbox" 
              v-model="editedWalkthrough.isOn" 
              :true-value="1" 
              :false-value="0"
            />
            Active
          </label>
        </div>
        
        <div class="form-actions">
          <button @click="saveWalkthrough" class="save-button" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- All Walkthroughs Grid View -->
    <div v-else class="landings-grid">
      <div v-for="landing in landings" :key="landing.id" class="landing-card" @click="fetchWalkthroughById(landing.id)">
        <div class="landing-header">
          <h2>{{ landing.title }}</h2>
          <h3>{{ landing.title_ar }}</h3>
        </div>
        <div class="landing-content">
          <p v-if="landing.description">{{ landing.description }}</p>
          <div v-if="landing.video" class="video-container">
            <video controls>
              <source :src="landing.video" type="video/mp4">
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
        <div class="landing-footer">
          <span class="status" :class="{ 'active': landing.isOn === 1 }">
            {{ landing.isOn === 1 ? 'Active' : 'Inactive' }}
          </span>
          <div class="dates">
            <p>Created: {{ formatDate(landing.created_at) }}</p>
            <p>Updated: {{ formatDate(landing.updated_at) }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';
import QuillEditor from '@/components/QuillEditor.vue';

export default {
  name: 'WalkthroughsView',
  components: {
    Sidebar,
    QuillEditor
  },
  data() {
    return {
      landings: [],
      singleWalkthrough: null,
      editedWalkthrough: {},
      loading: true,
      saving: false,
      error: null,
      searchId: '',
      isEditing: false,
      showCreateForm: false,
      newWalkthrough: {
        title: '',
        title_ar: '',
        description: '',
        video: '',
        isOn: 1
      },
      creating: false,
      toolbarOptions: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ 'header': 1 }, { 'header': 2 }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['clean'],
        ['link']
      ]
    };
  },
  created() {
    this.fetchAllWalkthroughs();
  },
  methods: {
    async fetchAllWalkthroughs() {
      try {
        this.loading = true;
        this.error = null;
        this.singleWalkthrough = null;
        this.searchId = '';
        this.isEditing = false;
        
        const token = localStorage.getItem('token'); // Assuming token is stored in localStorage
        const response = await axios.get('https://api.kewaar.com/api/admin/walkthroughs1', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.status === 'success') {
          this.landings = response.data.data.landings;
        } else {
          this.error = 'Failed to fetch walkthroughs data';
        }
      } catch (err) {
        this.error = err.message || 'An error occurred while fetching walkthroughs';
        console.error('Error fetching walkthroughs:', err);
      } finally {
        this.loading = false;
      }
    },
    
    async fetchWalkthroughById(id) {
      try {
        this.loading = true;
        this.error = null;
        this.isEditing = false;
        
        // If id is not provided, use the searchId from the input
        const walkthroughId = id || this.searchId;
        
        if (!walkthroughId) {
          this.error = 'Please enter a valid walkthrough ID';
          this.loading = false;
          return;
        }
        
        const token = localStorage.getItem('token');
        const response = await axios.get(`https://api.kewaar.com/api/admin/walkthroughs1/${walkthroughId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.status === 'success') {
          this.singleWalkthrough = response.data.data;
          // Initialize the edited walkthrough with the current values
          this.editedWalkthrough = { ...this.singleWalkthrough };
        } else {
          this.error = 'Failed to fetch walkthrough data';
        }
      } catch (err) {
        this.error = err.message || 'An error occurred while fetching the walkthrough';
        console.error('Error fetching walkthrough:', err);
      } finally {
        this.loading = false;
      }
    },
    
    toggleEditMode() {
      this.isEditing = !this.isEditing;
      if (this.isEditing) {
        // Reset the edited walkthrough to the current values when entering edit mode
        this.editedWalkthrough = { ...this.singleWalkthrough };
      }
    },
    
    async saveWalkthrough() {
      try {
        this.saving = true;
        this.error = null;
        
        // Get the token from localStorage
        const token = localStorage.getItem('token');
        
        // Log the token for debugging (remove in production)
        console.log('Using token:', token);
        
        // Check if we have a valid token
        if (!token) {
          this.error = 'Authentication token not found. Please log in again.';
          this.saving = false;
          return;
        }
        
        // Try using the same endpoint format as the fetch request (walkthroughs1)
        // This might be the issue - the fetch uses walkthroughs1 but update uses walkthroughs
        const endpoint = `https://api.kewaar.com/api/admin/walkthroughs1/${this.singleWalkthrough.id}`;
        console.log('Using endpoint:', endpoint);
        
        const response = await axios.put(
          endpoint,
          {
            isOn: this.editedWalkthrough.isOn,
            title: this.editedWalkthrough.title,
            title_ar: this.editedWalkthrough.title_ar,
            description: this.editedWalkthrough.description,
            video: this.editedWalkthrough.video
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        if (response.data.status === 'success') {
          // Update the single walkthrough with the edited values
          this.singleWalkthrough = { ...this.editedWalkthrough };
          this.isEditing = false;
          
          // Show success message or notification here if needed
          alert('Walkthrough updated successfully!');
        } else {
          this.error = 'Failed to update walkthrough';
        }
      } catch (err) {
        console.error('Error details:', err.response || err);
        
        if (err.response && err.response.status === 403) {
          this.error = 'Access denied. You may not have permission to edit this walkthrough. Please check your authentication token.';
          
          // Log more details about the error
          if (err.response.data) {
            console.error('Error response data:', err.response.data);
          }
          
          // Try to provide more helpful information
          console.log('Try using a fresh token by logging out and logging back in.');
          console.log('If the issue persists, check if your account has the necessary permissions.');
        } else {
          this.error = err.message || 'An error occurred while updating the walkthrough';
        }
      } finally {
        this.saving = false;
      }
    },
    
    async deleteWalkthrough(id) {
      if (!confirm('Are you sure you want to delete this walkthrough?')) {
        return;
      }

      try {
        this.loading = true;
        this.error = null;
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.delete(`https://api.kewaar.com/api/admin/walkthroughs1/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.status === 'success') {
          // Show success message
          alert('Walkthrough deleted successfully');
          // Refresh the list
          await this.fetchAllWalkthroughs();
        } else {
          this.error = 'Failed to delete walkthrough';
        }
      } catch (err) {
        console.error('Error deleting walkthrough:', err);
        this.error = err.message || 'An error occurred while deleting the walkthrough';
      } finally {
        this.loading = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    
    async createWalkthrough() {
      try {
        this.creating = true;
        this.error = null;
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.post('https://api.kewaar.com/api/admin/walkthroughs1', this.newWalkthrough, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (response.data.status === 'success') {
          // Show success message
          alert('Walkthrough created successfully');
          // Reset the form
          this.newWalkthrough = {
            title: '',
            title_ar: '',
            description: '',
            video: '',
            isOn: 1
          };
          // Close the form
          this.showCreateForm = false;
          // Refresh the list
          await this.fetchAllWalkthroughs();
        } else {
          this.error = 'Failed to create walkthrough';
        }
      } catch (err) {
        console.error('Error creating walkthrough:', err);
        this.error = err.message || 'An error occurred while creating the walkthrough';
      } finally {
        this.creating = false;
      }
    }
  }
};
</script>

<style scoped>
.walkthroughs-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  margin-left: 300px;
  min-height: 100vh;
  background-color: #f8fafc;
}

@media (max-width: 768px) {
  .walkthroughs-container {
    margin-left: 0;
    padding: 1rem;
  }
}

h1 {
  margin-bottom: 2rem;
  color: #1e293b;
  font-size: 2rem;
  font-weight: 600;
}

.search-section {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  align-items: center;
  background-color: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-box {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.search-box input {
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  width: 250px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.search-box input:focus {
  outline: none;
  border-color: #4a6cf7;
  box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.1);
}

.search-button, .view-all-button, .create-button {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
}

.search-button {
  background-color: #4a6cf7;
  color: white;
  border: none;
}

.search-button:hover {
  background-color: #3a5ce5;
  transform: translateY(-1px);
}

.view-all-button {
  background-color: #e2e8f0;
  color: #1e293b;
  border: none;
}

.view-all-button:hover {
  background-color: #cbd5e1;
  transform: translateY(-1px);
}

.create-button {
  background-color: #10b981;
  color: white;
  border: none;
}

.create-button:hover {
  background-color: #059669;
  transform: translateY(-1px);
}

.loading, .error {
  text-align: center;
  padding: 2rem;
  font-size: 1.125rem;
  background-color: white;
  border-radius: 12px;
  margin: 1rem 0;
}

.error {
  color: #dc2626;
  background-color: #fef2f2;
}

.landings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
  padding: 1rem 0;
}

.landing-card {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #e2e8f0;
}

.landing-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.landing-card.detailed {
  max-width: 900px;
  margin: 0 auto;
  cursor: default;
}

.landing-header {
  padding: 1.5rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.landing-header h2 {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  color: #1e293b;
  font-weight: 600;
}

.landing-header h3 {
  margin: 0;
  font-size: 1rem;
  color: #64748b;
  direction: rtl;
}

.landing-content {
  padding: 1.5rem;
}

.description {
  margin-bottom: 1.5rem;
  line-height: 1.6;
  color: #475569;
}

.video-container {
  margin-top: 1.5rem;
  border-radius: 8px;
  overflow: hidden;
}

.video-container video {
  width: 100%;
  border-radius: 8px;
}

.landing-footer {
  padding: 1.5rem;
  background-color: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status:not(.active) {
  background-color: #fef2f2;
  color: #dc2626;
}

.status.active {
  background-color: #f0fdf4;
  color: #16a34a;
}

.dates {
  font-size: 0.875rem;
  color: #64748b;
}

.dates p {
  margin: 0;
}

.back-button {
  margin-bottom: 1.5rem;
  color: #4a6cf7;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.back-button:hover {
  color: #3a5ce5;
  transform: translateX(-3px);
}

.edit-actions {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.toggle-button {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.edit-button {
  background-color: #4a6cf7;
  color: white;
}

.edit-button:hover {
  background-color: #3a5ce5;
  transform: translateY(-1px);
}

.cancel-button {
  background-color: #64748b;
  color: white;
}

.cancel-button:hover {
  background-color: #475569;
  transform: translateY(-1px);
}

.edit-form {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  position: relative;
  z-index: 100;
  max-height: 90vh;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #1e293b;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: #4a6cf7;
  box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.1);
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;
}

.checkbox-label input {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 4px;
  border: 2px solid #e2e8f0;
}

.form-actions {
  margin-top: 2rem;
  text-align: right;
}

.save-button {
  padding: 0.75rem 1.5rem;
  background-color: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.save-button:hover:not(:disabled) {
  background-color: #059669;
  transform: translateY(-1px);
}

.save-button:disabled {
  background-color: #94a3b8;
  cursor: not-allowed;
}

.delete-button {
  padding: 0.75rem 1.5rem;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.delete-button:hover {
  background-color: #dc2626;
  transform: translateY(-1px);
}

.create-form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.create-form-overlay .edit-form {
  margin: 0;
  max-width: 700px;
  width: 100%;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.form-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #1e293b;
  font-weight: 600;
}

.close-button {
  padding: 0.5rem 1rem;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.close-button:hover {
  background-color: #dc2626;
  transform: translateY(-1px);
}

.action-buttons {
  display: flex;
  gap: 1rem;
}
</style> 